import type { AccountGrantResult } from "pages/QuoteFlow/account";
import type { SessionRenewalOutcome } from "pages/StartLead/events";
import type {
  ConfirmedVia,
  MintAnswer,
  MintedGrant,
} from "pages/StartLead/submissionStatus";
import type { ResumeNext } from "pages/StartResume/resume";

import type { RenewalTarget } from "./login-grant-flow";

/** What replaced a grant that ended. */
export type Renewed =
  | { kind: "submission"; minted: MintedGrant; submissionId: string }
  | { kind: "account"; loginGrant: string };

/** What asking for a replacement came to: a new grant, the submission
 * confirmed on the spot, a wait before asking again, or no way to replace
 * it. */
export type RenewalResult =
  | { kind: "renewed"; renewed: Renewed }
  | {
      kind: "confirmed";
      next: ResumeNext;
      submissionId: string;
      via: ConfirmedVia;
    }
  | { kind: "wait"; rateLimited: boolean }
  | { kind: "unavailable" };

export interface RenewalClient {
  mint(submissionId: string): Promise<MintAnswer>;
  accountGrant(quoteId: string): Promise<AccountGrantResult>;
}

function submissionResult(
  answer: MintAnswer,
  submissionId: string,
): RenewalResult {
  switch (answer.kind) {
    case "minted":
      return {
        kind: "renewed",
        renewed: { kind: "submission", minted: answer.grant, submissionId },
      };
    case "confirmed":
      return {
        kind: "confirmed",
        next: answer.next,
        submissionId,
        via: answer.via,
      };
    case "rate_limited":
      return { kind: "wait", rateLimited: true };
    case "failed":
      return { kind: "wait", rateLimited: false };
    case "unavailable":
    case "missing":
      return { kind: "unavailable" };
  }
}

function accountResult(answer: AccountGrantResult): RenewalResult {
  switch (answer.kind) {
    case "granted":
      return {
        kind: "renewed",
        renewed: { kind: "account", loginGrant: answer.state.loginGrant },
      };
    case "rate_limited":
      return { kind: "wait", rateLimited: true };
    case "failed":
      return { kind: "wait", rateLimited: false };
    case "existing_account":
    case "not_available":
      return { kind: "unavailable" };
  }
}

/** Asks for a replacement for what the target names. */
export async function renewGrant(
  target: RenewalTarget,
  client: RenewalClient,
): Promise<RenewalResult> {
  if (target.kind === "account") {
    return accountResult(await client.accountGrant(target.quoteId));
  }
  return submissionResult(
    await client.mint(target.submissionId),
    target.submissionId,
  );
}

export function renewalOutcomeOf(result: RenewalResult): SessionRenewalOutcome {
  switch (result.kind) {
    case "renewed":
      return "minted";
    case "confirmed":
      return "confirmed";
    case "unavailable":
      return "unavailable";
    case "wait":
      return result.rateLimited ? "rate_limited" : "failed";
  }
}

/** What the screen says while it waits to ask again. */
export function renewalWaitCopy(rateLimited: boolean): {
  title: string;
  body: string;
} {
  return rateLimited
    ? { title: "Just a moment", body: "Please wait a moment, then try again." }
    : {
        title: "We couldn't send that just now.",
        body: "Please try again.",
      };
}
